import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiX, FiCalendar, FiCheck, FiAlertTriangle, FiClock, FiImage, FiDownload, FiFilter } = FiIcons;

const InverterHistory = ({ inverter, onClose, onRefresh }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [inverter.id]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('pv_inverter_history_a7b3c9d8e2')
        .select('*')
        .eq('inverter_id', inverter.id)
        .order('check_date', { ascending: false });

      if (error) {
        console.error('Error fetching inverter history:', error);
        return;
      }

      setHistory(data || []);
    } catch (error) {
      console.error('Exception fetching inverter history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandItem = (id) => {
    if (expandedItem === id) {
      setExpandedItem(null);
    } else {
      setExpandedItem(id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredHistory = filter === 'all' 
    ? history 
    : history.filter(item => 
        filter === 'passed' 
          ? item.check_result === 'Bestanden' 
          : item.check_result === 'Nicht bestanden'
      );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Prüfverlauf</h2>
            <p className="text-gray-600">{inverter.inverter_name} (SN: {inverter.serial_number})</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="p-6">
          {/* Filter */}
          <div className="flex items-center space-x-4 mb-6">
            <SafeIcon icon={FiFilter} className="text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Alle Ergebnisse</option>
              <option value="passed">Bestanden</option>
              <option value="failed">Nicht bestanden</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
            </div>
          ) : filteredHistory.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2 font-medium text-gray-600 text-sm">
                  <div>Datum</div>
                  <div className="hidden md:block">Zeit</div>
                  <div>Ergebnis</div>
                  <div>Details</div>
                </div>
              </div>
              
              {filteredHistory.map((item) => {
                const date = new Date(item.check_date);
                const formattedDate = date.toLocaleDateString('de-DE');
                const formattedTime = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                
                return (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className="grid grid-cols-3 md:grid-cols-4 gap-2 p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleExpandItem(item.id)}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiCalendar} className="mr-2 text-gray-500" />
                        <span>{formattedDate}</span>
                      </div>
                      <div className="hidden md:flex items-center">
                        <SafeIcon icon={FiClock} className="mr-2 text-gray-500" />
                        <span>{formattedTime}</span>
                      </div>
                      <div className="flex items-center">
                        <span
                          className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                            item.check_result === 'Bestanden'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          <SafeIcon
                            icon={item.check_result === 'Bestanden' ? FiCheck : FiAlertTriangle}
                            className="mr-1"
                          />
                          <span>{item.check_result}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-end">
                        {item.photo_url && (
                          <div className="flex items-center mr-3">
                            <SafeIcon icon={FiImage} className="text-blue-500" />
                          </div>
                        )}
                        <button
                          className="text-primary-600 hover:text-primary-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandItem(item.id);
                          }}
                        >
                          {expandedItem === item.id ? 'Weniger' : 'Mehr'}
                        </button>
                      </div>
                    </div>
                    
                    {expandedItem === item.id && (
                      <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <div className="space-y-4">
                          {item.notes && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Notizen</h4>
                              <p className="text-gray-600 bg-white p-3 rounded border border-gray-200">
                                {item.notes}
                              </p>
                            </div>
                          )}
                          
                          {item.photo_url && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Foto</h4>
                              <div className="relative">
                                <img
                                  src={item.photo_url}
                                  alt="Prüfungsfoto"
                                  className="w-full max-w-md rounded-lg shadow-sm"
                                />
                                <a
                                  href={item.photo_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-md text-blue-600 hover:text-blue-800"
                                  download
                                >
                                  <SafeIcon icon={FiDownload} />
                                </a>
                              </div>
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-500">
                            Prüfung durchgeführt am {formatDate(item.check_date)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">
                <SafeIcon icon={FiClock} className="mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {filter !== 'all'
                  ? `Keine "${filter === 'passed' ? 'Bestanden' : 'Nicht bestanden'}" Prüfungen gefunden`
                  : 'Keine Prüfungsdaten vorhanden'}
              </h3>
              <p className="text-gray-600 mb-4">
                {filter !== 'all'
                  ? 'Versuchen Sie einen anderen Filter'
                  : 'Die Prüfungshistorie wird angezeigt, sobald Prüfungen durchgeführt wurden.'}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Schließen
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InverterHistory;